/// <amd-module name="Repos/PullRequestActions/Components/ChangeTargetBranchPanel" />
import * as React from "react";
import { GitBaseVersionDescriptor, GitRepository, GitTargetVersionDescriptor } from "Repos/Common/Contracts/Git";
import { getReposRoutingService } from "Repos/Common/ReposRoutingService";
import { getChangeSummaryText } from "Repos/Common/Util/ChangeCounts";
import { branchToVersionDescriptor } from "Repos/Common/Util/Version";
import * as Resources from "Repos/PullRequestActions/Resources";
import { VersionDropdown } from "Repos/VersionDropdown/Components/VersionDropdown";
import { IVersionDropdownItem } from "Repos/VersionDropdown/Components/VersionDropdown.Props";
import { IReadonlyObservableValue, ObservableValue } from "VSS/Core/Observable";
import { format } from "VSS/Core/Util/String";
import { VssComponent } from "VSS/Platform/Layout";
import { IButtonProps } from "VSSUI/Button";
import { FormItem } from "VSSUI/FormItem";
import { Link } from "VSSUI/Link";
import { IListBoxItem } from "VSSUI/ListBox";
import { MessageCard, MessageCardSeverity } from "VSSUI/MessageCard";
import { Observer } from "VSSUI/Observer";
import { Panel } from "VSSUI/Panel";
import { Spinner } from "VSSUI/Spinner";
import { getGitClient } from "../RestClient/Git";

export interface IChangeTargetBranchResult {
    targetBranchName: string;
}

export interface IChangeTargetBranchPanelProps {
    errorMessage: IReadonlyObservableValue<string>;
    onDismiss: (result: IChangeTargetBranchResult | undefined) => void;
    originalTargetBranchName: string;
    repository: GitRepository;
    showOverlay: IReadonlyObservableValue<boolean>;
    sourceBranchName?: string;
}

const maxFileChanges = 200;

export class ChangeTargetBranchPanel extends VssComponent<IChangeTargetBranchPanelProps> {
    private targetBranchName = new ObservableValue<string>("");
    private fieldsValid = new ObservableValue(false);
    private previewMessage = new ObservableValue<React.ReactNode | undefined>(undefined);

    public render() {
        const { errorMessage, onDismiss, repository, showOverlay } = this.props;

        return (
            <Observer errorMessage={errorMessage} previewMessage={this.previewMessage} showOverlay={showOverlay} valid={this.fieldsValid}>
                {(props: { errorMessage?: string; previewMessage: React.ReactNode; showOverlay: boolean; valid: boolean }) => {
                    const footerButtons: IButtonProps[] = [
                        {
                            id: "cancel",
                            onClick: () => onDismiss(undefined),
                            text: Resources.Cancel
                        },
                        {
                            disabled: !props.valid,
                            id: "change",
                            onClick: () => onDismiss({ targetBranchName: this.targetBranchName.value }),
                            primary: true,
                            text: Resources.Change
                        }
                    ];

                    return (
                        <Panel
                            footerButtonProps={footerButtons}
                            onDismiss={() => onDismiss(undefined)}
                            overlayContent={props.showOverlay ? <Spinner /> : undefined}
                            titleProps={{ text: Resources.ChangeTargetBranch }}
                        >
                            <div className="flex-column flex-grow rhythm-vertical-16">
                                {props.errorMessage && <MessageCard severity={MessageCardSeverity.Error}>{props.errorMessage}</MessageCard>}

                                <FormItem label={Resources.NewTargetBranch}>
                                    <VersionDropdown
                                        onSelect={this.onTargetBranchChanged}
                                        placeholder={Resources.ChooseTargetBranch}
                                        providerOptions={{
                                            repository,
                                            viewTagsPivot: false
                                        }}
                                    />
                                </FormItem>
                                <div aria-live="polite" className="margin-top-16">
                                    {!!props.previewMessage && props.previewMessage}
                                </div>
                            </div>
                        </Panel>
                    );
                }}
            </Observer>
        );
    }

    private onTargetBranchChanged = (event: React.SyntheticEvent<HTMLElement>, item: IListBoxItem<IVersionDropdownItem> | null): void => {
        const { originalTargetBranchName, repository, sourceBranchName } = this.props;

        const targetBranchName = item && item.data ? item.data.versionDescriptor.version : "";
        this.targetBranchName.value = targetBranchName;
        if (this.previewMessage.value) {
            this.previewMessage.value = undefined;
        }

        if (originalTargetBranchName === targetBranchName) {
            this.previewMessage.value = format(Resources.NewTargetSameBranch, targetBranchName);
        } else if (sourceBranchName) {
            const baseVersion = branchToVersionDescriptor(targetBranchName);
            const targetVersion = branchToVersionDescriptor(sourceBranchName);

            getGitClient(this.context.pageContext)
                .getCommitDiffs(
                    repository.id,
                    repository.project.id,
                    true,
                    maxFileChanges,
                    0,
                    baseVersion as GitBaseVersionDescriptor,
                    targetVersion as GitTargetVersionDescriptor
                )
                .then(result => {
                    if (targetBranchName === this.targetBranchName.value) {
                        // If branches don't have a common ancestor, aheadCount/behindCount are not given
                        if (result && (result.aheadCount || !result.behindCount)) {
                            const compareUrl = getReposRoutingService(this.context.pageContext).getBranchCompareUrl(
                                repository.name,
                                repository.project.name,
                                baseVersion,
                                targetVersion
                            );

                            const changesMessage = result.aheadCount
                                ? format(Resources.NewTargetCommits, result.aheadCount, targetBranchName)
                                : format(Resources.NewTargetNoCommonAncestor, targetBranchName);

                            this.previewMessage.value = (
                                <div className="flex-column rhythm-vertical-4">
                                    <div>{changesMessage}</div>
                                    <div>
                                        {result.allChangesIncluded
                                            ? getChangeSummaryText(result.changes || [])
                                            : format(Resources.NewTargetMaxChanges, maxFileChanges)}
                                    </div>
                                    <Link href={compareUrl} target="_blank">
                                        {Resources.PreviewChanges}
                                    </Link>
                                </div>
                            );
                        } else {
                            this.previewMessage.value = Resources.NewTargetNoChanges;
                        }
                    }
                });
        }

        const valid = !!targetBranchName && targetBranchName !== originalTargetBranchName;
        if (this.fieldsValid.value !== valid) {
            this.fieldsValid.value = valid;
        }
    };
}
